import React, { useState, useEffect } from 'react'
import { 
  IonContent, 
  IonPage, 
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonItem,
  IonLabel,
  IonToggle,
  IonButton,
  IonToast,
  IonModal,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonButtons,
  IonInput,
  IonTextarea,
  IonNote,
  IonList,
  IonAlert,
  IonIcon,
  IonChip,
  IonBadge,
  IonSearchbar,
  IonSelect,
  IonSelectOption,
  IonPopover,
  IonActionSheet
} from '@ionic/react'
import { 
  linkOutline, 
  personOutline, 
  trashOutline, 
  addOutline,
  checkmarkCircleOutline,
  timeOutline,
  mailOutline,
  callOutline,
  arrowBackOutline,
  informationCircleOutline,
  settingsOutline,
  listOutline,
  starOutline,
  searchOutline,
  funnelOutline,
  personAddOutline
} from 'ionicons/icons'
import { useTranslation } from 'react-i18next'
import PageHeader from '../../components/PageHeader'
import { useAuthStore } from '../../store/authStore'
import { supabase } from '../../services/supabase'
import { CoachCoacheeRelationship } from '../../types'
import ProfilePictureUpload from '../../components/ProfilePictureUpload'
import Avatar from '../../components/Avatar'

const ProfilePage: React.FC = () => {
  const { t } = useTranslation()
  const { user, setUser, logout } = useAuthStore()
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')

  // Edit Profile Modal State
  const [showEditModal, setShowEditModal] = useState(false)
  const [isEditingSaving, setIsEditingSaving] = useState(false)
  const [editFormData, setEditFormData] = useState({
    full_name: user?.full_name || '',
    specializations: user?.specializations ? user.specializations.join(', ') : '',
    contact_details: user?.contact_details || ''
  })

  // Delete Account Modal State
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [isDeletingAccount, setIsDeletingAccount] = useState(false)
  const [deleteConfirmation, setDeleteConfirmation] = useState('')

  // Coach Linking State (for coachees)
  const [coachRelationships, setCoachRelationships] = useState<(CoachCoacheeRelationship & { 
    coach_name?: string, 
    coach_email?: string,
    coach_specializations?: string[],
    coach_contact_details?: string,
    coach_avatar_url?: string,
    coach_profile_picture_visibility?: string
  })[]>([])
  const [showLinkCoachModal, setShowLinkCoachModal] = useState(false)
  const [invitationCode, setInvitationCode] = useState('')
  const [isLinkingCoach, setIsLinkingCoach] = useState(false)
  const [showRemoveLinkAlert, setShowRemoveLinkAlert] = useState(false)
  const [relationshipToRemove, setRelationshipToRemove] = useState<string | null>(null)
  const [showCoachDetailsModal, setShowCoachDetailsModal] = useState(false)
  const [selectedCoachRelationship, setSelectedCoachRelationship] = useState<any>(null)
  
  // Search and filter states for coaches
  const [coachSearchTerm, setCoachSearchTerm] = useState('')
  const [coachStatusFilter, setCoachStatusFilter] = useState<'all' | 'active' | 'pending' | 'inactive'>('all')
  
  // Status dropdown states
  const [showStatusActionSheet, setShowStatusActionSheet] = useState(false)
  const [selectedRelationshipForStatus, setSelectedRelationshipForStatus] = useState<any>(null)

  // Fetch coach relationships for coachees
  const fetchCoachRelationships = async () => {
    if (!user || user.role !== 'coachee') return

    try {
      const { data, error } = await supabase
        .from('coach_coachee_relationships')
        .select(`
          *,
          coach:profiles!coach_coachee_relationships_coach_id_fkey (
            full_name,
            email,
            specializations,
            contact_details,
            avatar_url,
            profile_picture_url,
            profile_picture_visibility
          )
        `)
        .eq('coachee_id', user.id)
        .order('started_at', { ascending: false })

      if (error) {
        console.error('Supabase error fetching coach relationships:', error)
        throw error
      }

      console.log('🔍 DEBUG: Raw coach relationships data:', data)
      console.log('🔍 DEBUG: Total relationships found:', data?.length || 0)

      const relationshipsWithCoachInfo = data?.map((rel: any) => ({
        ...rel,
        coach_name: rel.coach?.full_name || rel.coach?.email || 'Unknown Coach',
        coach_email: rel.coach?.email,
        coach_specializations: rel.coach?.specializations,
        coach_contact_details: rel.coach?.contact_details,
        coach_avatar_url: rel.coach?.profile_picture_url || rel.coach?.avatar_url,
        coach_profile_picture_visibility: rel.coach?.profile_picture_visibility
      })) || []

      console.log('🔍 DEBUG: Processed coach relationships:', relationshipsWithCoachInfo)
      setCoachRelationships(relationshipsWithCoachInfo)
    } catch (error) {
      console.error('Error fetching coach relationships:', error)
    }
  }

  // Link with coach using invitation code
  const handleLinkWithCoach = async () => {
    if (!user || !invitationCode.trim()) return

    setIsLinkingCoach(true)
    try {
      // Validate invitation code
      const { data: codeData, error: codeError } = await supabase
        .from('invitation_codes')
        .select('*')
        .eq('code', invitationCode.trim())
        .gt('expires_at', new Date().toISOString())
        .single()

      if (codeError || !codeData) {
        throw new Error(t('profile.coachLinking.invalidCode'))
      }

      // Check if code has remaining uses
      if (codeData.current_uses >= codeData.max_uses || codeData.used) {
        throw new Error(t('profile.coachLinking.codeExhausted'))
      }

      // Additional check for single-use codes that are already accepted
      if (codeData.max_uses === 1 && codeData.status === 'accepted') {
        throw new Error(t('profile.coachLinking.codeAlreadyUsed'))
      }

      // Check if relationship already exists
      const { data: existingRel } = await supabase
        .from('coach_coachee_relationships')
        .select('id')
        .eq('coach_id', codeData.coach_id)
        .eq('coachee_id', user.id)
        .single()

      if (existingRel) {
        throw new Error(t('profile.coachLinking.alreadyLinked'))
      }

      // Try the new safe function first (bypasses RLS issues)
      console.log('🔍 Attempting to link with coach using safe function...')
      console.log('📋 Code data:', codeData)
      console.log('👤 User ID:', user.id)
      
      const { data: relationshipResult, error: relationshipError } = await supabase
        .rpc('create_coach_coachee_relationship', {
          p_coach_id: codeData.coach_id,
          p_coachee_id: user.id
        })

      console.log('🔄 Safe function result:', relationshipResult)
      console.log('❌ Safe function error:', relationshipError)

      if (relationshipError || !relationshipResult?.success) {
        const errorMsg = relationshipError?.message || relationshipResult?.error || 'Unknown error'
        console.warn('⚠️ Safe function failed, trying direct method:', errorMsg)
        
        // Fallback to direct insert method
        const { error: directError } = await supabase
          .from('coach_coachee_relationships')
          .insert([{
            coach_id: codeData.coach_id,
            coachee_id: user.id,
            status: 'active'
          }])

        if (directError) throw directError

        // Manually update invitation code usage (fallback method)
        const { error: invitationUpdateError } = await supabase
          .from('invitation_codes')
          .update({
            used: true,
            status: 'accepted',
            used_by: user.id,
            used_at: new Date().toISOString()
          })
          .eq('id', codeData.id)

        if (invitationUpdateError) {
          console.error('❌ Failed to update invitation code status (fallback):', invitationUpdateError)
          // Don't throw here - relationship was created successfully
        } else {
          console.log('✅ Invitation code updated successfully (fallback method)')
        }
      } else {
        console.log('✅ Relationship created successfully via safe function:', relationshipResult)
        console.log('🎉 Both relationship and invitation code should be updated automatically!')
      }

      setToastMessage(t('profile.coachLinking.linkSuccess'))
      setShowToast(true)
      setShowLinkCoachModal(false)
      setInvitationCode('')
      fetchCoachRelationships()
    } catch (error: any) {
      console.error('Error linking with coach:', error)
      setToastMessage(error.message || t('profile.coachLinking.linkError'))
      setShowToast(true)
    } finally {
      setIsLinkingCoach(false)
    }
  }

  // Remove coach relationship
  const handleRemoveCoachLink = async () => {
    if (!relationshipToRemove) return

    try {
      const { error } = await supabase
        .from('coach_coachee_relationships')
        .delete()
        .eq('id', relationshipToRemove)

      if (error) throw error

      setToastMessage(t('profile.coachLinking.removeSuccess'))
      setShowToast(true)
      setShowRemoveLinkAlert(false)
      setRelationshipToRemove(null)
      fetchCoachRelationships()
    } catch (error) {
      console.error('Error removing coach link:', error)
      setToastMessage(t('profile.coachLinking.removeError'))
      setShowToast(true)
    }
  }

  // Handle status change
  const handleStatusChange = async (relationshipId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('coach_coachee_relationships')
        .update({ status: newStatus })
        .eq('id', relationshipId)

      if (error) throw error

      setToastMessage(t('profile.coachLinking.statusUpdated', 'Status updated successfully'))
      setShowToast(true)
      fetchCoachRelationships()
    } catch (error) {
      console.error('Error updating status:', error)
      setToastMessage(t('profile.coachLinking.statusUpdateError', 'Failed to update status'))
      setShowToast(true)
    }
  }

  // Load coach relationships on component mount
  useEffect(() => {
    if (user?.role === 'coachee') {
      fetchCoachRelationships()
    }
  }, [user])

  // Handle hash fragment navigation (e.g., #my-coaches)
  useEffect(() => {
    const hash = window.location.hash.substring(1) // Remove # symbol
    if (hash) {
      // Small delay to ensure the component is fully rendered
      setTimeout(() => {
        const element = document.getElementById(hash)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
      }, 100)
    }
  }, [])

  // Filter coach relationships based on search and status
  const filteredCoachRelationships = coachRelationships.filter(relationship => {
    const matchesSearch = !coachSearchTerm || 
      relationship.coach_name?.toLowerCase().includes(coachSearchTerm.toLowerCase()) ||
      relationship.coach_email?.toLowerCase().includes(coachSearchTerm.toLowerCase()) ||
      relationship.coach_specializations?.some(spec => 
        spec.toLowerCase().includes(coachSearchTerm.toLowerCase())
      )
    
    const matchesStatus = coachStatusFilter === 'all' || relationship.status === coachStatusFilter
    
    return matchesSearch && matchesStatus
  })

  const handleToggleMotivationalMessages = async (enabled: boolean) => {
    if (!user) return

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ show_motivational_messages: enabled })
        .eq('id', user.id)

      if (error) throw error

      setUser({
        ...user,
        show_motivational_messages: enabled
      })

      setToastMessage(
        enabled 
          ? t('profile.motivationalMessagesEnabled')
          : t('profile.motivationalMessagesDisabled')
      )
      setShowToast(true)
    } catch (error) {
      console.error('Error updating motivational messages setting:', error)
      setToastMessage(t('common.error'))
      setShowToast(true)
    }
  }

  const openEditModal = () => {
    setEditFormData({
      full_name: user?.full_name || '',
      specializations: user?.specializations ? user.specializations.join(', ') : '',
      contact_details: user?.contact_details || ''
    })
    setShowEditModal(true)
  }

  const handleEditProfile = async () => {
    if (!user) return

    setIsEditingSaving(true)
    try {
      const updateData: any = {
        full_name: editFormData.full_name.trim()
      }

      // Only include coach-specific fields if user is a coach
      if (user.role === 'coach') {
        updateData.specializations = editFormData.specializations
          ? editFormData.specializations.split(',').map((s: string) => s.trim()).filter((s: string) => s)
          : []
        updateData.contact_details = editFormData.contact_details.trim()
      }

      const { error } = await supabase
        .from('profiles')
        .update(updateData)
        .eq('id', user.id)

      if (error) throw error

      // Update user state
      setUser({
        ...user,
        ...updateData
      })

      setToastMessage(t('profile.editProfileModal.saved'))
      setShowToast(true)
      setShowEditModal(false)
    } catch (error) {
      console.error('Error updating profile:', error)
      setToastMessage(t('profile.editProfileModal.error'))
      setShowToast(true)
    } finally {
      setIsEditingSaving(false)
    }
  }

  const handleDeleteAccount = async () => {
    if (!user) return
    
    // Check confirmation text (always use 'DELETE' for consistency)
    if (deleteConfirmation !== 'DELETE') {
      setToastMessage(t('profile.deleteAccountModal.confirmationMismatch'))
      setShowToast(true)
      return
    }

    setIsDeletingAccount(true)
    try {
      // Delete user account using Supabase Auth
      const { error } = await supabase.auth.admin.deleteUser(user.id)

      if (error) throw error

      setToastMessage(t('profile.deleteAccountModal.success'))
      setShowToast(true)
      setShowDeleteModal(false)
      
      // Logout user after successful deletion
      setTimeout(() => {
        logout()
      }, 2000)
    } catch (error) {
      console.error('Error deleting account:', error)
      setToastMessage(t('profile.deleteAccountModal.error'))
      setShowToast(true)
    } finally {
      setIsDeletingAccount(false)
    }
  }

  const handleProfilePictureUpdate = async (pictureUrl?: string, visibility?: 'public' | 'private' | 'none') => {
    if (user) {
      console.log('Updating profile picture in state:', { pictureUrl, visibility });
      
      // Update the local state immediately for responsive UI
      const updatedUser = {
        ...user,
        profile_picture_url: pictureUrl,
        profile_picture_visibility: visibility
      };
      setUser(updatedUser);
      
      // Also refresh from database to ensure consistency
      try {
        const { data: freshUserData, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', user.id)
          .single();
          
        if (!error && freshUserData) {
          console.log('Refreshed user data from database:', freshUserData);
          setUser({
            ...user,
            ...freshUserData
          });
        }
      } catch (error) {
        console.warn('Could not refresh user data from database:', error);
      }
    }
  }

  return (
    <IonPage>
      <PageHeader title={t('profile.title')} />
      <IonContent>
        <div className="max-w-4xl mx-auto px-2 sm:px-4 py-4">
          <h1 className="hidden sm:block text-2xl font-heading font-bold text-gray-900 mb-6">
            {t('profile.yourProfile')}
          </h1>

          {/* Profile Picture */}
          {user && (
            <ProfilePictureUpload 
              user={user}
              onUpdate={handleProfilePictureUpdate}
              className="mb-4"
            />
          )}

          {/* User Information */}
          <IonCard className="mb-4">
            <IonCardHeader>
              <IonCardTitle className="flex items-center text-base sm:text-lg">
                <IonIcon icon={informationCircleOutline} className="mr-2 flex-shrink-0" />
                {t('profile.information')}
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonItem>
                <IonLabel>
                  <h3>{t('profile.name')}</h3>
                  <p>{user?.full_name}</p>
                </IonLabel>
              </IonItem>
              <IonItem>
                <IonLabel>
                  <h3>{t('profile.email')}</h3>
                  <p>{user?.email}</p>
                </IonLabel>
              </IonItem>
              <IonItem>
                <IonLabel>
                  <h3>{t('profile.role')}</h3>
                  <p>{user?.role}</p>
                </IonLabel>
              </IonItem>
              {user?.role === 'coach' && user?.specializations && user.specializations.length > 0 && (
                <IonItem>
                  <IonLabel>
                    <h3>{t('profile.editProfileModal.specializationsLabel')}</h3>
                    <p>{user.specializations.join(', ')}</p>
                  </IonLabel>
                </IonItem>
              )}
              {user?.role === 'coach' && user?.contact_details && (
                <IonItem>
                  <IonLabel>
                    <h3>{t('profile.editProfileModal.contactDetailsLabel')}</h3>
                    <p>{user.contact_details}</p>
                  </IonLabel>
                </IonItem>
              )}
            </IonCardContent>
          </IonCard>

          {/* Preferences */}
          <IonCard className="mb-4">
            <IonCardHeader>
              <IonCardTitle className="flex items-center text-base sm:text-lg">
                <IonIcon icon={settingsOutline} className="mr-2 flex-shrink-0" />
                {t('profile.preferences')}
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <IonItem>
                <IonLabel>
                  <h3>{t('profile.motivationalMessages')}</h3>
                  <p>{t('profile.motivationalMessagesDescription')}</p>
                </IonLabel>
                <IonToggle
                  checked={user?.show_motivational_messages || false}
                  onIonChange={(e) => handleToggleMotivationalMessages(e.detail.checked)}
                  aria-label={t('profile.motivationalMessages')}
                  slot="end"
                />
              </IonItem>
            </IonCardContent>
          </IonCard>

          {/* Coach Relationships (only for coachees) */}
          {user?.role === 'coachee' && (
            <IonCard className="mb-4" id="my-coaches">
              <IonCardHeader>
                <IonCardTitle className="flex items-center justify-between text-base sm:text-lg">
                  <div className="flex items-center">
                    <IonIcon icon={linkOutline} className="mr-2 flex-shrink-0" />
                    {t('profile.coachLinking.title')}
                  </div>
                  <IonButton 
                    fill="clear" 
                    size="small"
                    onClick={() => setShowLinkCoachModal(true)}
                  >
                    <IonIcon icon={personAddOutline} className="sm:mr-2" />
                    <span className="hidden sm:inline">{t('profile.coachLinking.linkNewCoach')}</span>
                  </IonButton>
                </IonCardTitle>
              </IonCardHeader>
              <IonCardContent>
                {/* Search and Filter Controls */}
                {coachRelationships.length > 0 && (
                  <div className="mb-4 space-y-3">
                    <IonSearchbar
                      value={coachSearchTerm}
                      onIonInput={(e) => setCoachSearchTerm(e.detail.value!)}
                      placeholder={t('profile.coachLinking.searchPlaceholder', 'Search coaches...')}
                      showClearButton="focus"
                      className="p-0 text-sm"
                    />
                    <IonItem className="text-sm">
                      <IonIcon icon={funnelOutline} slot="start" className="text-sm" />
                      <IonLabel className="text-sm">{t('profile.coachLinking.filterByStatus', 'Filter by status')}</IonLabel>
                      <IonSelect
                        value={coachStatusFilter}
                        onIonChange={(e) => setCoachStatusFilter(e.detail.value)}
                        interface="popover"
                        className="text-sm"
                      >
                        <IonSelectOption value="all" className="text-sm">{t('profile.coachLinking.statusAll', 'All')}</IonSelectOption>
                        <IonSelectOption value="active" className="text-sm">{t('profile.coachLinking.statusActive', 'Active')}</IonSelectOption>
                        <IonSelectOption value="pending" className="text-sm">{t('profile.coachLinking.statusPending', 'Pending')}</IonSelectOption>
                        <IonSelectOption value="inactive" className="text-sm">{t('profile.coachLinking.statusInactive', 'Inactive')}</IonSelectOption>
                      </IonSelect>
                    </IonItem>
                  </div>
                )}

                {filteredCoachRelationships.length > 0 ? (
                  <IonList>
                    {filteredCoachRelationships.map((relationship) => (
                      <IonItem key={relationship.id} className="mb-2">
                        <div slot="start">
                          <Avatar
                            user={{
                              id: relationship.coach_id,
                              full_name: relationship.coach_name,
                              email: relationship.coach_email,
                              profile_picture_url: relationship.coach_avatar_url,
                              profile_picture_visibility: relationship.coach_profile_picture_visibility as 'public' | 'private' | 'none' | undefined
                            }}
                            currentUserId={user?.id}
                            relationship="coachee"
                            size="medium"
                          />
                        </div>
                        <IonLabel>
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-medium text-lg">{relationship.coach_name}</h3>
                            <IonChip 
                              color={relationship.status === 'active' ? 'success' : 
                                     relationship.status === 'pending' ? 'warning' : 'medium'}
                              className="text-xs cursor-pointer"
                              onClick={() => {
                                setSelectedRelationshipForStatus(relationship)
                                setShowStatusActionSheet(true)
                              }}
                            >
                              {relationship.status.charAt(0).toUpperCase() + relationship.status.slice(1)}
                            </IonChip>
                          </div>
                          <div className="space-y-1 mt-1">
                            <p className="text-sm text-gray-600 flex items-center">
                              <IonIcon icon={mailOutline} className="mr-2 text-xs" />
                              {relationship.coach_email}
                            </p>
                            {relationship.coach_contact_details && (
                              <p className="text-sm text-gray-600 flex items-center">
                                <IonIcon icon={callOutline} className="mr-2 text-xs" />
                                {relationship.coach_contact_details}
                              </p>
                            )}
                          </div>
                          {relationship.coach_specializations && relationship.coach_specializations.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {relationship.coach_specializations.slice(0, 3).map((spec, index) => (
                                <IonChip key={index} className="text-xs" color="primary">
                                  {spec}
                                </IonChip>
                              ))}
                              {relationship.coach_specializations.length > 3 && (
                                <IonChip className="text-xs" color="medium">
                                  +{relationship.coach_specializations.length - 3} more
                                </IonChip>
                              )}
                            </div>
                          )}
                          <p className="text-xs text-gray-500 mt-2 flex items-center">
                            <IonIcon icon={timeOutline} className="mr-1" />
                            {t('profile.coachLinking.linkedSince')}: {new Date(relationship.started_at).toLocaleDateString()}
                          </p>
                        </IonLabel>
                      </IonItem>
                    ))}
                  </IonList>
                ) : coachRelationships.length > 0 ? (
                  <div className="text-center py-8">
                    <IonIcon icon={searchOutline} className="text-4xl text-gray-400 mb-4" />
                    <p className="text-gray-500 mb-4">{t('profile.coachLinking.noMatchingCoaches', 'No coaches match your search criteria')}</p>
                    <IonButton 
                      fill="outline" 
                      size="small"
                      onClick={() => {
                        setCoachSearchTerm('')
                        setCoachStatusFilter('all')
                      }}
                    >
                      <IonIcon icon={funnelOutline} slot="start" />
                      {t('profile.coachLinking.clearFilters', 'Clear Filters')}
                    </IonButton>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <IonIcon icon={linkOutline} className="text-4xl text-gray-400 mb-4" />
                    <p className="text-gray-500 mb-4">{t('profile.coachLinking.noCoaches')}</p>
                    <IonButton 
                      fill="outline" 
                      size="small"
                      onClick={() => setShowLinkCoachModal(true)}
                    >
                      <IonIcon icon={personAddOutline} slot="start" />
                      {t('profile.coachLinking.linkFirstCoach')}
                    </IonButton>
                  </div>
                )}

                {/* Privacy Note */}
                <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-800">
                    <IonIcon icon={checkmarkCircleOutline} className="mr-1" />
                    <strong>{t('profile.coachLinking.privacyNote')}</strong> {t('profile.coachLinking.privacyDescription')}
                  </p>
                </div>
              </IonCardContent>
            </IonCard>
          )}

          {/* Actions */}
          <IonCard>
            <IonCardHeader>
              <IonCardTitle className="flex items-center text-base sm:text-lg">
                <IonIcon icon={settingsOutline} className="mr-2 flex-shrink-0" />
                {t('profile.actions')}
              </IonCardTitle>
            </IonCardHeader>
            <IonCardContent>
              <div className="space-y-3">
                <IonButton 
                  expand="block" 
                  fill="outline"
                  onClick={openEditModal}
                >
                  {t('profile.editProfile')}
                </IonButton>
                <IonButton 
                  expand="block" 
                  fill="outline"
                  color="danger"
                  onClick={() => setShowDeleteModal(true)}
                >
                  {t('profile.deleteAccount')}
                </IonButton>
              </div>
            </IonCardContent>
          </IonCard>
        </div>

        {/* Link with Coach Modal */}
        <IonModal isOpen={showLinkCoachModal} onDidDismiss={() => setShowLinkCoachModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton onClick={() => setShowLinkCoachModal(false)}>
                  <IonIcon icon={arrowBackOutline} />
                </IonButton>
              </IonButtons>
              <IonTitle>{t('profile.coachLinking.linkModal.title')}</IonTitle>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <div className="mb-4">
              <h2 className="text-lg font-semibold mb-2">
                {t('profile.coachLinking.linkModal.subtitle')}
              </h2>
              <p className="text-gray-600 mb-4">
                {t('profile.coachLinking.linkModal.description')}
              </p>
            </div>

            <IonCard>
              <IonCardContent>
                <div className="text-sm text-gray-600 space-y-2 mb-4">
                  <p><strong>1.</strong> {t('profile.coachLinking.linkModal.step1')}</p>
                  <p><strong>2.</strong> {t('profile.coachLinking.linkModal.step2')}</p>
                  <p><strong>3.</strong> {t('profile.coachLinking.linkModal.step3')}</p>
                </div>
              </IonCardContent>
            </IonCard>

            <div className="mt-6">
              <IonItem>
                <IonLabel position="stacked">
                  {t('profile.coachLinking.linkModal.codeLabel')}
                </IonLabel>
                <IonInput
                  value={invitationCode}
                  placeholder={t('profile.coachLinking.linkModal.codePlaceholder')}
                  onIonInput={(e) => setInvitationCode(e.detail.value!)}
                />
              </IonItem>

              <IonButton
                expand="block"
                className="mt-4"
                onClick={handleLinkWithCoach}
                disabled={isLinkingCoach || !invitationCode.trim()}
              >
                {isLinkingCoach ? t('profile.coachLinking.linkModal.linking') : t('profile.coachLinking.linkModal.linkButton')}
              </IonButton>
            </div>

            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-xs text-blue-800">
                <strong>{t('profile.coachLinking.linkModal.note')}</strong> {t('profile.coachLinking.linkModal.noteDescription')}
              </p>
            </div>
          </IonContent>
        </IonModal>

        {/* Edit Profile Modal */}
        <IonModal isOpen={showEditModal} onDidDismiss={() => setShowEditModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonButtons slot="start">
                <IonButton onClick={() => setShowEditModal(false)}>
                  <IonIcon icon={arrowBackOutline} />
                </IonButton>
              </IonButtons>
              <IonTitle>{t('profile.editProfileModal.title')}</IonTitle>
              <IonButtons slot="end" className="hidden sm:flex">
                <IonButton onClick={() => setShowEditModal(false)}>
                  {t('common.cancel')}
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <IonList>
              <IonItem>
                <IonLabel position="stacked">
                  {t('profile.editProfileModal.fullNameLabel')}
                </IonLabel>
                <IonInput
                  value={editFormData.full_name}
                  placeholder={t('profile.editProfileModal.fullNamePlaceholder')}
                  onIonInput={(e) => setEditFormData({
                    ...editFormData,
                    full_name: e.detail.value!
                  })}
                />
              </IonItem>

              <IonItem>
                <IonLabel position="stacked">
                  {t('profile.editProfileModal.emailLabel')}
                </IonLabel>
                <IonInput
                  value={user?.email}
                  disabled
                />
                <IonNote slot="helper">
                  {t('profile.editProfileModal.emailNote')}
                </IonNote>
              </IonItem>

              {user?.role === 'coach' && (
                <>
                  <IonItem>
                    <IonLabel position="stacked">
                      {t('profile.editProfileModal.specializationsLabel')}
                    </IonLabel>
                    <IonInput
                      value={editFormData.specializations}
                      placeholder={t('profile.editProfileModal.specializationsPlaceholder')}
                      onIonInput={(e) => setEditFormData({
                        ...editFormData,
                        specializations: e.detail.value!
                      })}
                    />
                    <IonNote slot="helper">
                      {t('profile.editProfileModal.specializationsNote')}
                    </IonNote>
                  </IonItem>

                  <IonItem>
                    <IonLabel position="stacked">
                      {t('profile.editProfileModal.contactDetailsLabel')}
                    </IonLabel>
                    <IonTextarea
                      value={editFormData.contact_details}
                      placeholder={t('profile.editProfileModal.contactDetailsPlaceholder')}
                      rows={3}
                      onIonInput={(e) => setEditFormData({
                        ...editFormData,
                        contact_details: e.detail.value!
                      })}
                    />
                    <IonNote slot="helper">
                      {t('profile.editProfileModal.contactDetailsNote')}
                    </IonNote>
                  </IonItem>
                </>
              )}
            </IonList>

            <div className="mt-6">
              <IonButton
                expand="block"
                onClick={handleEditProfile}
                disabled={isEditingSaving || !editFormData.full_name.trim()}
              >
                {isEditingSaving ? t('profile.editProfileModal.saving') : t('common.save')}
              </IonButton>
            </div>
          </IonContent>
        </IonModal>

        {/* Delete Account Modal */}
        <IonModal isOpen={showDeleteModal} onDidDismiss={() => setShowDeleteModal(false)}>
          <IonHeader>
            <IonToolbar color="danger">
              <IonButtons slot="start">
                <IonButton onClick={() => setShowDeleteModal(false)}>
                  <IonIcon icon={arrowBackOutline} />
                </IonButton>
              </IonButtons>
              <IonTitle>{t('profile.deleteAccountModal.title')}</IonTitle>
              <IonButtons slot="end" className="hidden sm:flex">
                <IonButton onClick={() => setShowDeleteModal(false)}>
                  {t('common.cancel')}
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            <div className="text-center mb-4">
              <h2 className="text-lg font-semibold text-red-600 mb-2">
                ⚠️ {t('profile.deleteAccountModal.warning')}
              </h2>
              <p className="text-gray-600 mb-4">
                {t('profile.deleteAccountModal.description')}
              </p>
            </div>

            <IonCard>
              <IonCardContent>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                    Profile information
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                    Coaching sessions
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                    Goals and tasks
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                    Reflection entries
                  </li>
                  <li className="flex items-center">
                    <span className="w-2 h-2 bg-red-500 rounded-full mr-3"></span>
                    Messages and communications
                  </li>
                </ul>
              </IonCardContent>
            </IonCard>

            <div className="mt-6">
              <IonItem>
                <IonLabel position="stacked">
                  {t('profile.deleteAccountModal.confirmationText')}
                </IonLabel>
                <IonInput
                  value={deleteConfirmation}
                  placeholder={t('profile.deleteAccountModal.confirmationPlaceholder')}
                  onIonInput={(e) => setDeleteConfirmation(e.detail.value!)}
                />
              </IonItem>

              <IonButton
                expand="block"
                color="danger"
                className="mt-4"
                onClick={handleDeleteAccount}
                disabled={isDeletingAccount || !deleteConfirmation}
              >
                {isDeletingAccount ? t('profile.deleteAccountModal.deleting') : t('profile.deleteAccountModal.deleteButton')}
              </IonButton>
            </div>
          </IonContent>
        </IonModal>

        {/* Coach Details Modal */}
        <IonModal isOpen={showCoachDetailsModal} onDidDismiss={() => setShowCoachDetailsModal(false)}>
          <IonHeader>
            <IonToolbar>
              <IonTitle>{t('profile.coachDetails.title', 'Coach Details')}</IonTitle>
              <IonButtons slot="end">
                <IonButton onClick={() => setShowCoachDetailsModal(false)}>
                  {t('common.close')}
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>
          <IonContent className="ion-padding">
            {selectedCoachRelationship && (
              <div className="space-y-6">
                {/* Coach Profile */}
                <IonCard>
                  <IonCardContent>
                    <div className="flex items-start space-x-4">
                      <Avatar
                        user={{
                          id: selectedCoachRelationship.coach_id,
                          full_name: selectedCoachRelationship.coach_name,
                          email: selectedCoachRelationship.coach_email,
                          profile_picture_url: selectedCoachRelationship.coach_avatar_url
                        }}
                        currentUserId={user?.id}
                        relationship="coachee"
                        size="extra-large"
                      />
                      <div className="flex-1">
                        <h2 className="text-xl font-bold mb-2">{selectedCoachRelationship.coach_name}</h2>
                        <div className="space-y-2">
                          <p className="text-gray-600 flex items-center">
                            <IonIcon icon={mailOutline} className="mr-2" />
                            {selectedCoachRelationship.coach_email}
                          </p>
                          {selectedCoachRelationship.coach_contact_details && (
                            <p className="text-gray-600 flex items-center">
                              <IonIcon icon={callOutline} className="mr-2" />
                              {selectedCoachRelationship.coach_contact_details}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </IonCardContent>
                </IonCard>

                {/* Specializations */}
                {selectedCoachRelationship.coach_specializations && selectedCoachRelationship.coach_specializations.length > 0 && (
                  <IonCard>
                    <IonCardHeader>
                      <IonCardTitle className="flex items-center text-base sm:text-lg">
                        <IonIcon icon={starOutline} className="mr-2 flex-shrink-0" />
                        {t('profile.coachDetails.specializations', 'Specializations')}
                      </IonCardTitle>
                    </IonCardHeader>
                    <IonCardContent>
                      <div className="flex flex-wrap gap-2">
                        {selectedCoachRelationship.coach_specializations.map((spec: string, index: number) => (
                          <IonChip key={index} color="primary">
                            {spec}
                          </IonChip>
                        ))}
                      </div>
                    </IonCardContent>
                  </IonCard>
                )}

                {/* Relationship Info */}
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle className="flex items-center text-base sm:text-lg">
                      <IonIcon icon={listOutline} className="mr-2 flex-shrink-0" />
                      {t('profile.coachDetails.relationshipInfo', 'Relationship Information')}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <div className="space-y-3">
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('profile.coachLinking.linkedSince')}:</span>
                        <span className="font-medium">{new Date(selectedCoachRelationship.started_at).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">{t('profile.coachDetails.status', 'Status')}:</span>
                        <IonChip color={selectedCoachRelationship.status === 'active' ? 'success' : 'medium'}>
                          {selectedCoachRelationship.status}
                        </IonChip>
                      </div>
                      {selectedCoachRelationship.notes && (
                        <div>
                          <p className="text-gray-600 mb-1">{t('profile.coachDetails.notes', 'Notes')}:</p>
                          <p className="text-gray-800">{selectedCoachRelationship.notes}</p>
                        </div>
                      )}
                    </div>
                  </IonCardContent>
                </IonCard>

                {/* Actions */}
                <IonCard>
                  <IonCardHeader>
                    <IonCardTitle className="flex items-center text-base sm:text-lg">
                      <IonIcon icon={settingsOutline} className="mr-2 flex-shrink-0" />
                      {t('profile.coachDetails.actions', 'Actions')}
                    </IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <div className="space-y-3">
                      {selectedCoachRelationship.coach_email && (
                        <IonButton 
                          expand="block" 
                          fill="outline"
                          href={`mailto:${selectedCoachRelationship.coach_email}`}
                        >
                          <IonIcon icon={mailOutline} slot="start" />
                          {t('profile.coachDetails.sendEmail', 'Send Email')}
                        </IonButton>
                      )}
                      <IonButton 
                        expand="block" 
                        fill="outline"
                        color="danger"
                        onClick={() => {
                          setRelationshipToRemove(selectedCoachRelationship.id)
                          setShowRemoveLinkAlert(true)
                          setShowCoachDetailsModal(false)
                        }}
                      >
                        <IonIcon icon={trashOutline} slot="start" />
                        {t('profile.coachDetails.removeRelationship', 'Remove Relationship')}
                      </IonButton>
                    </div>
                  </IonCardContent>
                </IonCard>
              </div>
            )}
          </IonContent>
        </IonModal>

        {/* Status Action Sheet */}
        <IonActionSheet
          isOpen={showStatusActionSheet}
          onDidDismiss={() => setShowStatusActionSheet(false)}
          header={t('profile.coachLinking.changeStatus', 'Change Status')}
          buttons={[
            {
              text: t('profile.coachLinking.statusActive', 'Active'),
              icon: checkmarkCircleOutline,
              handler: () => {
                if (selectedRelationshipForStatus) {
                  handleStatusChange(selectedRelationshipForStatus.id, 'active')
                }
              }
            },
            {
              text: t('profile.coachLinking.statusPending', 'Pending'),
              icon: timeOutline,
              handler: () => {
                if (selectedRelationshipForStatus) {
                  handleStatusChange(selectedRelationshipForStatus.id, 'pending')
                }
              }
            },
            {
              text: t('profile.coachLinking.statusInactive', 'Inactive'),
              icon: timeOutline,
              handler: () => {
                if (selectedRelationshipForStatus) {
                  handleStatusChange(selectedRelationshipForStatus.id, 'inactive')
                }
              }
            },
            {
              text: t('profile.coachLinking.removeCoach', 'Remove Coach'),
              role: 'destructive',
              icon: trashOutline,
              handler: () => {
                if (selectedRelationshipForStatus) {
                  setRelationshipToRemove(selectedRelationshipForStatus.id)
                  setShowRemoveLinkAlert(true)
                }
              }
            },
            {
              text: t('common.cancel'),
              role: 'cancel'
            }
          ]}
        />

        {/* Remove Coach Link Alert */}
        <IonAlert
          isOpen={showRemoveLinkAlert}
          onDidDismiss={() => setShowRemoveLinkAlert(false)}
          header={t('profile.coachLinking.removeAlert.title')}
          message={t('profile.coachLinking.removeAlert.message')}
          buttons={[
            {
              text: t('common.cancel'),
              role: 'cancel'
            },
            {
              text: t('profile.coachLinking.removeAlert.confirmButton'),
              handler: handleRemoveCoachLink
            }
          ]}
        />

        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={2000}
          position="bottom"
        />
      </IonContent>
    </IonPage>
  )
}

export default ProfilePage 